/**
 * AD-526 + остаток AD-519 + AD-527 (решения Сергея №38, №39, №41). Время
 * пункта повестки — одно определение «изменено» и одна проверка для всех
 * дверей экрана: карточки (`EventAgenda`), редактора повестки
 * (`AgendaTimeline`) и мастера (`planProgramWrite`).
 *
 * Зеркало серверного `BE src/modules/events/utils/agenda-item-time.util.ts`:
 * решает сервер, а клиент отказывает ДО запроса ровно там же, где откажет
 * сервер. Мастер пишет программу поштучно, и отказ посреди записи оставил бы
 * её частично сохранённой.
 */

export interface AgendaItemMoments {
  startTime: string | Date;
  endTime: string | Date;
}

const instantOf = (value: string | Date): number => new Date(value).getTime();

/**
 * «Время пункта изменено» — итоговые моменты отличаются от сохранённых хотя бы
 * в одном конце. Правка названия или описания — не изменение. Неразборный
 * момент считается изменением: проверка запустится и откажет, а не пропустит
 * его как «прежний».
 */
export const agendaItemMomentsChanged = (
  stored: AgendaItemMoments,
  next: AgendaItemMoments,
): boolean => {
  // `NaN === NaN` ложно: неразборный момент сам собой даёт «изменено».
  const same = (a: string | Date, b: string | Date): boolean =>
    instantOf(a) === instantOf(b);
  return !(
    same(stored.startTime, next.startTime) && same(stored.endTime, next.endTime)
  );
};

/** Конец строго позже начала — моменты, а не строки (`AD-519`). */
export const isAgendaItemOrderValid = (
  startTime: string | Date,
  endTime: string | Date,
): boolean => {
  const start = instantOf(startTime);
  const end = instantOf(endTime);
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return end > start;
};

/** Даты события и его пояс — всё, что нужно проверке дней (`AD-527`). */
export interface AgendaEventDays {
  startDate: string | Date;
  endDate: string | Date;
  /** Имя пояса IANA; `null`/нет — пояс не известен. */
  timezone?: string | null;
}

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** AD-527 (решение №41 п.4). Окончание — не позже 06:00 после последнего дня. */
export const AGENDA_NIGHT_TOLERANCE_MS = 6 * HOUR_MS;

/** AD-527 (решение №41). Пояс не известен — границы шире на 14 ч в обе стороны. */
export const AGENDA_UNKNOWN_ZONE_SLACK_MS = 14 * HOUR_MS;

const mod = (value: number, divisor: number): number =>
  ((value % divisor) + divisor) % divisor;

/** Смещение пояса в момент `instant`: местное время минус UTC, в мс. */
const zoneOffsetMs = (instant: number, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(instant));
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value);
  const wallClockAsUtc = Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    part("hour"),
    part("minute"),
    part("second"),
  );
  return wallClockAsUtc - (instant - mod(instant, 1000));
};

/**
 * Момент местной полуночи, отстоящей на `dayShift` суток от дня, в котором по
 * поясу лежит `instant`. Смещение уточняется в самой полуночи: на стыке
 * перехода на летнее время оно другое, чем в `instant`.
 */
const zonedMidnight = (
  instant: number,
  timeZone: string,
  dayShift: number,
): number => {
  const local = instant + zoneOffsetMs(instant, timeZone);
  const localMidnight = local - mod(local, DAY_MS) + dayShift * DAY_MS;
  const guess = localMidnight - zoneOffsetMs(localMidnight, timeZone);
  return localMidnight - zoneOffsetMs(guess, timeZone);
};

/**
 * Границы дней события: `[firstDayStart, lastDayEnd)` для начала пункта,
 * `lastDayEnd + 06:00` — для окончания. `null` — начало события не разобрано.
 * Отдельно от проверки, чтобы пикер времени ставил те же пределы.
 */
export const agendaEventDayBounds = (
  event: AgendaEventDays,
): { firstDayStart: number; lastDayEnd: number; latestEnd: number } | null => {
  const eventStart = instantOf(event.startDate);
  const eventEndRaw = instantOf(event.endDate);
  if (Number.isNaN(eventStart)) return null;
  // Окончание не разобрано или раньше начала — один день: выдумывать
  // длительность нельзя (то же правило, что у мастера, `countProgramDays`).
  const eventEnd =
    Number.isNaN(eventEndRaw) || eventEndRaw < eventStart
      ? eventStart
      : eventEndRaw;
  const { timezone } = event;
  const firstDayStart = timezone
    ? zonedMidnight(eventStart, timezone, 0)
    : eventStart - mod(eventStart, DAY_MS) - AGENDA_UNKNOWN_ZONE_SLACK_MS;
  const lastDayEnd = timezone
    ? zonedMidnight(eventEnd, timezone, 1)
    : eventEnd - mod(eventEnd, DAY_MS) + DAY_MS + AGENDA_UNKNOWN_ZONE_SLACK_MS;
  return {
    firstDayStart,
    lastDayEnd,
    latestEnd: lastDayEnd + AGENDA_NIGHT_TOLERANCE_MS,
  };
};

/**
 * AD-527 (решения №39, №41). Пункт — в днях события: начало не раньше
 * полуночи первого дня и раньше полуночи после последнего, окончание — не
 * позже 06:00 после последнего дня. Зеркало серверного
 * `isAgendaItemWithinEventDays`.
 */
export const isAgendaItemWithinEventDays = (
  next: AgendaItemMoments,
  event: AgendaEventDays,
): boolean => {
  const itemStart = instantOf(next.startTime);
  const itemEnd = instantOf(next.endTime);
  const bounds = agendaEventDayBounds(event);
  if (!bounds || Number.isNaN(itemStart) || Number.isNaN(itemEnd)) return false;
  return (
    itemStart >= bounds.firstDayStart &&
    itemStart < bounds.lastDayEnd &&
    itemEnd <= bounds.latestEnd
  );
};

/** Почему время пункта не годится: порядок моментов или дни события. */
export type AgendaItemTimeViolation = "order" | "outsideEventDays";

/**
 * ОДНА проверка времени пункта для всех дверей экрана — карточки
 * (`EventAgenda`), редактора повестки (`AgendaTimeline`) и мастера
 * (`planProgramWrite`). Зеркало серверного `agendaItemTimeViolation`: со
 * `stored` (правка) проверка идёт только если время изменено; порядок — раньше
 * дней.
 */
export const agendaItemTimeViolation = (
  next: AgendaItemMoments,
  event: AgendaEventDays,
  stored?: AgendaItemMoments,
): AgendaItemTimeViolation | null => {
  if (stored && !agendaItemMomentsChanged(stored, next)) return null;
  if (!isAgendaItemOrderValid(next.startTime, next.endTime)) return "order";
  if (!isAgendaItemWithinEventDays(next, event)) return "outsideEventDays";
  return null;
};

/**
 * Форма имени пояса IANA — та же, что у сервера (`IANA_TIME_ZONE_NAME`):
 * с буквы, без голого смещения.
 */
const IANA_TIME_ZONE_NAME = /^[A-Za-z][A-Za-z0-9_+\-/]*$/;

/**
 * AD-527 (решение №41). Пояс браузера для тела события. `undefined`, если
 * движок отдал не имя IANA (голое смещение, `Etc/Unknown`) или имя, которое сам
 * не принимает: такое значение сервер отверг бы (`@IsTimeZone`), и событие не
 * сохранилось бы вовсе. Без пояса сервер считает событие «пояс не известен».
 */
export const browserTimeZone = (): string | undefined => {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!zone || !IANA_TIME_ZONE_NAME.test(zone) || zone === "Etc/Unknown") {
    return undefined;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone;
  } catch {
    return undefined;
  }
};
